import React, { useState } from 'react';
import { 
  PawPrint, User, Mail, Phone, MapPin, 
  Building, Plus, X, Check, Loader2, ArrowRight, Wallet, ShieldCheck, RefreshCw,
  FileText, Send, CheckCircle, Map
} from 'lucide-react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Standard-Tarif: Tierarzt im eigenen Wohnort
const PAYMENT_PLANS_STANDARD = [
  { id: 'quarterly', label: 'Quartal', price: 30, originalPrice: 30, months: 3, saving: 0 },
  { id: 'halfyearly', label: 'Halbjährlich', price: 57, originalPrice: 60, months: 6, saving: 3 },
  { id: 'yearly', label: 'Jährlich', price: 110, originalPrice: 120, months: 12, saving: 10, recommended: true }
];

// Nachbarort-Tarif: Tierarzt in einem Nachbarort
const PAYMENT_PLANS_NACHBARORT = [
  { id: 'quarterly', label: 'Quartal', price: 36, originalPrice: 36, months: 3, saving: 0 },
  { id: 'halfyearly', label: 'Halbjährlich', price: 68, originalPrice: 72, months: 6, saving: 4 },
  { id: 'yearly', label: 'Jährlich', price: 132, originalPrice: 144, months: 12, saving: 12, recommended: true }
];

const ADDITIONAL_PET_MONTHLY_PRICE_STANDARD = 8.50;
const ADDITIONAL_PET_MONTHLY_PRICE_NACHBARORT = 10.50;

const MembershipRegistrationForm = ({ onClose }) => {
  // Neuer Ablauf: 1: Personal, 2: Pets, 3: Payment, 4: Summary, 5: Success
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    street: '',
    houseNumber: '',
    postalCode: '',
    city: '',
    pets: [{ id: Date.now(), petType: '', petName: '', notes: '' }],
    paymentPlan: 'yearly',
    paymentMethod: 'manual',
    autoRenewal: true,
    tariffType: 'standard', // NEU: Tariftyp
    agbAccepted: false,
    datenschutzAccepted: false
  });

  // Dynamische Preisberechnung basierend auf Tarif
  const getPaymentPlans = () => {
    return formData.tariffType === 'nachbarort' ? PAYMENT_PLANS_NACHBARORT : PAYMENT_PLANS_STANDARD;
  };

  const getAdditionalPetPrice = () => {
    return formData.tariffType === 'nachbarort' ? ADDITIONAL_PET_MONTHLY_PRICE_NACHBARORT : ADDITIONAL_PET_MONTHLY_PRICE_STANDARD;
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const addPet = () => {
    setFormData(prev => ({
      ...prev,
      pets: [...prev.pets, { id: Date.now(), petType: '', petName: '', notes: '' }]
    }));
  };

  const removePet = (id) => {
    if (formData.pets.length > 1) {
      setFormData(prev => ({
        ...prev,
        pets: prev.pets.filter(p => p.id !== id)
      }));
    }
  };

  const updatePet = (id, field, value) => {
    setFormData(prev => ({
      ...prev,
      pets: prev.pets.map(p => p.id === id ? { ...p, [field]: value } : p)
    }));
  };

  const calculatePrice = () => {
    const plans = getPaymentPlans();
    const plan = plans.find(p => p.id === formData.paymentPlan);
    if (!plan) return { total: 0, base: 0, additional: 0 };
    
    const additionalPets = Math.max(0, formData.pets.length - 1);
    const additionalPetPrice = getAdditionalPetPrice();
    const additionalCost = additionalPets * additionalPetPrice * plan.months;
    
    return {
      base: plan.price,
      additional: additionalCost,
      total: plan.price + additionalCost,
      additionalPets,
      months: plan.months
    };
  };

  const validateStep = (stepNum) => {
    if (stepNum === 1) {
      if (!formData.name || !formData.email || !formData.phone) {
        toast.error('Bitte füllen Sie alle Pflichtfelder aus');
        return false;
      }
      if (!formData.email.includes('@')) {
        toast.error('Bitte geben Sie eine gültige E-Mail-Adresse ein');
        return false;
      }
    }
    if (stepNum === 2) {
      const validPets = formData.pets.filter(p => p.petType && p.petName);
      if (validPets.length === 0) {
        toast.error('Bitte fügen Sie mindestens ein Tier hinzu');
        return false;
      }
    }
    if (stepNum === 3) {
      if (!formData.agbAccepted || !formData.datenschutzAccepted) {
        toast.error('Bitte akzeptieren Sie die AGB und Datenschutzerklärung');
        return false;
      }
    }
    return true;
  };

  const handleNext = () => {
    if (validateStep(step)) {
      setStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setStep(prev => prev - 1);
  };

  // Registrierung absenden - sendet E-Mail mit Bestätigungslink
  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const validPets = formData.pets.filter(p => p.petType && p.petName);
      
      const response = await axios.post(`${BACKEND_URL}/api/membership/register-pending`, {
        name: formData.name,
        email: formData.email.toLowerCase().trim(),
        phone: formData.phone,
        street: formData.street,
        houseNumber: formData.houseNumber,
        postalCode: formData.postalCode,
        city: formData.city,
        pets: validPets,
        paymentPlan: formData.paymentPlan,
        paymentMethod: formData.paymentMethod,
        autoRenewal: formData.autoRenewal,
        tariffType: formData.tariffType  // NEU: Tariftyp mitsenden
      });

      if (response.data.success) {
        setRegistrationComplete(true);
        setStep(5);
        toast.success('Vertragsunterlagen wurden per E-Mail gesendet!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler bei der Registrierung');
    } finally {
      setIsLoading(false);
    }
  };

  const priceInfo = calculatePrice();
  const selectedPlan = getPaymentPlans().find(p => p.id === formData.paymentPlan);

  return (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader className="bg-gradient-to-r from-orange-500 to-blue-600 text-white rounded-t-lg">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-xl flex items-center gap-2">
              <PawPrint className="h-6 w-6" />
              Dom's Pfotenflatrate
            </CardTitle>
            <CardDescription className="text-orange-100">
              {step <= 4 ? `Schritt ${step} von 4` : 'Abgeschlossen'}
            </CardDescription>
          </div>
          {onClose && (
            <button onClick={onClose} className="text-white/80 hover:text-white">
              <X className="h-6 w-6" />
            </button>
          )}
        </div>
        
        {/* Progress Bar */}
        {step <= 4 && (
          <div className="flex gap-2 mt-4">
            {[1, 2, 3, 4].map((s) => (
              <div
                key={s}
                className={`h-2 flex-1 rounded-full transition-all ${
                  s <= step ? 'bg-white' : 'bg-white/30'
                }`}
              />
            ))}
          </div>
        )}
      </CardHeader>

      <CardContent className="p-6">
        {/* Step 1: Persönliche Daten */}
        {step === 1 && (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <User className="h-12 w-12 mx-auto text-blue-500 mb-3" />
              <h3 className="text-lg font-semibold">Persönliche Daten</h3>
              <p className="text-gray-600 text-sm">Bitte geben Sie Ihre Kontaktdaten ein</p>
            </div>

            <div className="grid gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <User className="h-4 w-4" /> Name *
                </Label>
                <Input
                  value={formData.name}
                  onChange={(e) => updateField('name', e.target.value)}
                  placeholder="Max Mustermann"
                />
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Mail className="h-4 w-4" /> E-Mail *
                </Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => updateField('email', e.target.value)}
                  placeholder="ihre@email.de"
                />
                <p className="text-xs text-gray-500">
                  An diese Adresse werden die Vertragsunterlagen und der Bestätigungslink gesendet.
                </p>
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Phone className="h-4 w-4" /> Telefon *
                </Label>
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => updateField('phone', e.target.value)}
                  placeholder="01234 567890"
                />
              </div>

              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2 space-y-2">
                  <Label className="flex items-center gap-2">
                    <MapPin className="h-4 w-4" /> Straße
                  </Label>
                  <Input
                    value={formData.street}
                    onChange={(e) => updateField('street', e.target.value)}
                    placeholder="Musterstraße"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nr.</Label>
                  <Input
                    value={formData.houseNumber}
                    onChange={(e) => updateField('houseNumber', e.target.value)}
                    placeholder="12"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2">
                <div className="space-y-2">
                  <Label>PLZ</Label>
                  <Input
                    value={formData.postalCode}
                    onChange={(e) => updateField('postalCode', e.target.value)}
                    placeholder="58089"
                  />
                </div>
                <div className="col-span-2 space-y-2">
                  <Label className="flex items-center gap-2">
                    <Building className="h-4 w-4" /> Stadt
                  </Label>
                  <Input
                    value={formData.city}
                    onChange={(e) => updateField('city', e.target.value)}
                    placeholder="Hagen"
                  />
                </div>
              </div>
            </div>

            <Button onClick={handleNext} className="w-full">
              Weiter <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        )}

        {/* Step 2: Haustiere */}
        {step === 2 && (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <PawPrint className="h-12 w-12 mx-auto text-orange-500 mb-3" />
              <h3 className="text-lg font-semibold">Ihre Tiere</h3>
              <p className="text-gray-600 text-sm">Welche Tiere sollen in der Flatrate aufgenommen werden?</p>
            </div>

            {formData.pets.map((pet, index) => (
              <div key={pet.id} className="p-4 border rounded-xl bg-gray-50 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="font-medium text-gray-700">Tier {index + 1}</span>
                  {formData.pets.length > 1 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removePet(pet.id)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Tierart *</Label>
                    <select
                      value={pet.petType}
                      onChange={(e) => updatePet(pet.id, 'petType', e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg"
                    >
                      <option value="">Auswählen...</option>
                      <option value="Hund">Hund</option>
                      <option value="Katze">Katze</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label>Name *</Label>
                    <Input
                      value={pet.petName}
                      onChange={(e) => updatePet(pet.id, 'petName', e.target.value)}
                      placeholder="Bello"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Besonderheiten (optional)</Label>
                  <Input
                    value={pet.notes}
                    onChange={(e) => updatePet(pet.id, 'notes', e.target.value)}
                    placeholder="z.B. ängstlich, benötigt Transportbox"
                  />
                </div>
              </div>
            ))}

            <Button
              variant="outline"
              onClick={addPet}
              className="w-full border-dashed"
            >
              <Plus className="h-4 w-4 mr-2" /> Weiteres Tier hinzufügen (+{getAdditionalPetPrice().toFixed(2).replace('.', ',')}€/Monat)
            </Button>

            <div className="flex gap-3">
              <Button variant="outline" onClick={handleBack} className="flex-1">
                Zurück
              </Button>
              <Button onClick={handleNext} className="flex-1">
                Weiter <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Zahlungsoptionen */}
        {step === 3 && (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <Wallet className="h-12 w-12 mx-auto text-green-500 mb-3" />
              <h3 className="text-lg font-semibold">Tarif, Zahlungsplan & Vertragsoptionen</h3>
            </div>

            {/* NEU: Tarifauswahl */}
            <div className="space-y-3">
              <Label className="flex items-center gap-2">
                <Map className="h-4 w-4" /> Tarif wählen
              </Label>
              <p className="text-xs text-gray-500 -mt-1">Wo befindet sich Ihr Haustierarzt?</p>
              <div className="grid gap-3">
                <label
                  className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    formData.tariffType === 'standard' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    checked={formData.tariffType === 'standard'}
                    onChange={() => updateField('tariffType', 'standard')}
                    className="mt-1 h-5 w-5"
                  />
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Standard-Tarif</span>
                      <span className="text-sm font-bold text-blue-600">ab 10€/Monat</span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">Tierarzt im eigenen Wohnort</p>
                    <p className="text-xs text-gray-500 mt-1">Ideal, wenn Ihr Tierarzt in derselben Stadt wie Sie wohnt.</p>
                  </div>
                </label>

                <label
                  className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    formData.tariffType === 'nachbarort' ? 'border-orange-500 bg-orange-50' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    checked={formData.tariffType === 'nachbarort'}
                    onChange={() => updateField('tariffType', 'nachbarort')}
                    className="mt-1 h-5 w-5"
                  />
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Nachbarort-Tarif</span>
                      <span className="text-sm font-bold text-orange-600">ab 12€/Monat</span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">Tierarzt in einem Nachbarort</p>
                    <p className="text-xs text-gray-500 mt-1">Für alle, deren Tierarzt in einer Nachbarstadt ansässig ist.</p>
                  </div>
                </label>
              </div>
            </div>

            {/* Zahlungsplan Auswahl */}
            <div className="space-y-3">
              <Label>Zahlungsplan wählen</Label>
              <div className="grid gap-3">
                {getPaymentPlans().map((plan) => (
                  <label
                    key={plan.id}
                    className={`flex items-center justify-between p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      formData.paymentPlan === plan.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <input
                        type="radio"
                        name="paymentPlan"
                        checked={formData.paymentPlan === plan.id}
                        onChange={() => updateField('paymentPlan', plan.id)}
                        className="h-5 w-5 text-blue-600"
                      />
                      <div>
                        <span className="font-medium">{plan.label}</span>
                        {plan.saving > 0 && (
                          <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                            {plan.saving}€ sparen
                          </span>
                        )}
                        {plan.recommended && (
                          <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                            Empfohlen
                          </span>
                        )}
                      </div>
                    </div>
                    <span className="font-bold text-lg">{plan.price}€</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Zahlungsmethode */}
            <div className="space-y-3">
              <Label>Zahlungsmethode</Label>
              <div className="p-4 rounded-xl border-2 border-blue-500 bg-blue-50 text-center">
                <Wallet className="h-8 w-8 mx-auto mb-2 text-blue-600" />
                <span className="font-medium">Überweisung</span>
                <p className="text-xs text-gray-500 mt-1">Sie erhalten die Bankdaten per E-Mail</p>
              </div>
            </div>

            {/* Vertragsverlängerung */}
            <div className="space-y-3">
              <Label className="flex items-center gap-2">
                <RefreshCw className="h-4 w-4" /> Vertragsverlängerung
              </Label>
              
              <label
                className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  formData.autoRenewal ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                }`}
              >
                <input
                  type="radio"
                  checked={formData.autoRenewal === true}
                  onChange={() => updateField('autoRenewal', true)}
                  className="mt-1 h-5 w-5"
                />
                <div>
                  <span className="font-medium">Automatische Verlängerung</span>
                  <p className="text-sm text-gray-600">Verlängert sich automatisch, wenn nicht 4 Wochen vorher gekündigt.</p>
                </div>
              </label>

              <label
                className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  !formData.autoRenewal ? 'border-orange-500 bg-orange-50' : 'border-gray-200'
                }`}
              >
                <input
                  type="radio"
                  checked={formData.autoRenewal === false}
                  onChange={() => updateField('autoRenewal', false)}
                  className="mt-1 h-5 w-5"
                />
                <div>
                  <span className="font-medium">Keine automatische Verlängerung</span>
                  <p className="text-sm text-gray-600">Vertrag endet automatisch nach einem Jahr.</p>
                </div>
              </label>
            </div>

            {/* AGB & Datenschutz */}
            <div className="space-y-3 bg-gray-50 p-4 rounded-xl">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.agbAccepted}
                  onChange={(e) => updateField('agbAccepted', e.target.checked)}
                  className="mt-1 h-5 w-5"
                />
                <span className="text-sm text-gray-700">
                  Ich akzeptiere die <a href="/agb" target="_blank" className="text-blue-600 underline">AGB</a> *
                </span>
              </label>

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.datenschutzAccepted}
                  onChange={(e) => updateField('datenschutzAccepted', e.target.checked)}
                  className="mt-1 h-5 w-5"
                />
                <span className="text-sm text-gray-700">
                  Ich akzeptiere die <a href="/datenschutz" target="_blank" className="text-blue-600 underline">Datenschutzerklärung</a> *
                </span>
              </label>
            </div>

            {/* Hinweis Leistungsbeginn */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-800">
                <strong>⚠️ Hinweis:</strong> Der Leistungsbeginn liegt <strong>14 Tage nach Vertragsabschluss</strong>. 
                Diese Wartezeit dient dem Schutz vor Missbrauch.
              </p>
            </div>

            <div className="flex gap-3">
              <Button variant="outline" onClick={handleBack} className="flex-1">
                Zurück
              </Button>
              <Button onClick={handleNext} className="flex-1">
                Zur Übersicht <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 4: Zusammenfassung */}
        {step === 4 && (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <FileText className="h-12 w-12 mx-auto text-purple-500 mb-3" />
              <h3 className="text-lg font-semibold">Zusammenfassung</h3>
              <p className="text-gray-600 text-sm">Bitte überprüfen Sie Ihre Angaben</p>
            </div>

            {/* Persönliche Daten */}
            <div className="bg-gray-50 rounded-xl p-4 space-y-2">
              <h4 className="font-semibold text-gray-700 flex items-center gap-2">
                <User className="h-4 w-4" /> Persönliche Daten
              </h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <span className="text-gray-600">Name:</span>
                <span className="font-medium">{formData.name}</span>
                <span className="text-gray-600">E-Mail:</span>
                <span className="font-medium">{formData.email}</span>
                <span className="text-gray-600">Telefon:</span>
                <span className="font-medium">{formData.phone}</span>
                {formData.street && (
                  <>
                    <span className="text-gray-600">Adresse:</span>
                    <span className="font-medium">
                      {formData.street} {formData.houseNumber}, {formData.postalCode} {formData.city}
                    </span>
                  </>
                )}
              </div>
            </div>

            {/* Tiere */}
            <div className="bg-orange-50 rounded-xl p-4 space-y-2">
              <h4 className="font-semibold text-gray-700 flex items-center gap-2">
                <PawPrint className="h-4 w-4" /> Ihre Tiere ({formData.pets.filter(p => p.petName).length})
              </h4>
              <div className="space-y-1">
                {formData.pets.filter(p => p.petName).map((pet, index) => (
                  <div key={pet.id} className="text-sm">
                    <span className="font-medium">{pet.petName}</span>
                    <span className="text-gray-600"> ({pet.petType})</span>
                    {pet.notes && <span className="text-gray-500"> - {pet.notes}</span>}
                  </div>
                ))}
              </div>
            </div>

            {/* Zahlungsdetails */}
            <div className="bg-green-50 rounded-xl p-4 space-y-2">
              <h4 className="font-semibold text-gray-700 flex items-center gap-2">
                <Wallet className="h-4 w-4" /> Zahlungsdetails
              </h4>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span>Tarif:</span>
                  <span className={`font-medium ${formData.tariffType === 'nachbarort' ? 'text-orange-600' : 'text-blue-600'}`}>
                    {formData.tariffType === 'nachbarort' ? 'Nachbarort-Tarif' : 'Standard-Tarif'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Zahlungsplan:</span>
                  <span className="font-medium">{selectedPlan?.label}</span>
                </div>
                <div className="flex justify-between">
                  <span>Grundpreis:</span>
                  <span>{priceInfo.base}€</span>
                </div>
                {priceInfo.additionalPets > 0 && (
                  <div className="flex justify-between">
                    <span>Zusätzliche Tiere ({priceInfo.additionalPets}x):</span>
                    <span>+{priceInfo.additional.toFixed(2)}€</span>
                  </div>
                )}
                <hr className="my-2" />
                <div className="flex justify-between font-bold text-lg">
                  <span>Gesamtbetrag:</span>
                  <span className="text-green-600">{priceInfo.total.toFixed(2)}€</span>
                </div>
              </div>
            </div>

            {/* Vertragsoptionen */}
            <div className="bg-blue-50 rounded-xl p-4">
              <h4 className="font-semibold text-gray-700 flex items-center gap-2 mb-2">
                <RefreshCw className="h-4 w-4" /> Vertragsoption
              </h4>
              <p className="text-sm">
                {formData.autoRenewal 
                  ? '✅ Automatische Verlängerung (kann bis 4 Wochen vor Vertragsende gekündigt werden)'
                  : '⏹️ Keine automatische Verlängerung (Vertrag endet nach 1 Jahr)'}
              </p>
            </div>

            {/* Hinweis */}
            <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <Send className="h-6 w-6 text-purple-600 mt-0.5" />
                <div>
                  <p className="font-semibold text-purple-800">Was passiert als nächstes?</p>
                  <p className="text-sm text-purple-700 mt-1">
                    Nach dem Absenden erhalten Sie eine E-Mail mit:
                  </p>
                  <ul className="text-sm text-purple-700 mt-2 space-y-1">
                    <li>• Ihren vollständigen Vertragsunterlagen</li>
                    <li>• Den Bankdaten für die Überweisung</li>
                    <li>• Einem <strong>Bestätigungslink</strong> zur Vertragsaktivierung</li>
                  </ul>
                  <p className="text-sm text-purple-700 mt-2">
                    Der Vertrag wird erst nach Klick auf den Bestätigungslink gültig.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <Button variant="outline" onClick={handleBack} className="flex-1">
                Zurück
              </Button>
              <Button 
                onClick={handleSubmit} 
                disabled={isLoading}
                className="flex-1 bg-gradient-to-r from-orange-500 to-blue-600 hover:from-orange-600 hover:to-blue-700"
              >
                {isLoading ? (
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                ) : (
                  <Send className="h-5 w-5 mr-2" />
                )}
                Vertragsunterlagen anfordern
              </Button>
            </div>
          </div>
        )}

        {/* Step 5: Erfolg */}
        {step === 5 && (
          <div className="text-center space-y-6 py-6">
            <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="h-12 w-12 text-green-500" />
            </div>
            
            <div>
              <h3 className="text-xl font-bold text-green-600 mb-2">
                E-Mail wurde gesendet!
              </h3>
              <p className="text-gray-600">
                Ihre Vertragsunterlagen wurden an <strong>{formData.email}</strong> gesendet.
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 text-left">
              <h4 className="font-semibold text-blue-800 mb-3">📬 So geht's weiter:</h4>
              <ol className="text-sm text-blue-700 space-y-3">
                <li className="flex items-start gap-3">
                  <span className="bg-blue-200 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold shrink-0">1</span>
                  <span>Öffnen Sie die E-Mail mit Ihren Vertragsunterlagen</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-blue-200 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold shrink-0">2</span>
                  <span>Prüfen Sie alle Angaben in Ruhe</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-blue-200 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold shrink-0">3</span>
                  <span><strong>Klicken Sie auf den Bestätigungslink</strong> um den Vertrag abzuschließen</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-blue-200 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold shrink-0">4</span>
                  <span>Überweisen Sie den Betrag mit Ihrer Mitgliedsnummer</span>
                </li>
              </ol>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
              <strong>💡 Keine E-Mail erhalten?</strong> Prüfen Sie Ihren Spam-Ordner oder kontaktieren Sie uns unter doms.pfotenexpress@gmail.com
            </div>

            {onClose && (
              <Button onClick={onClose} className="w-full">
                Schließen
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MembershipRegistrationForm;
